import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { usePackageOfferings } from "@/providers/ContentProvider";
import { useScheduledMeetings } from "@/providers/ScheduledMeetingsProvider";
import { useToast } from "@/hooks/use-toast";
import { ServiceSelectionStep } from "./ServiceSelectionStep";
import { QuestionnaireStep } from "./QuestionnaireStep";
import { MeetingScheduleStep } from "./MeetingScheduleStep";
import type { AvailabilitySlot } from "@/domain/configurator";
import type { BudgetRange } from "@/domain/intake";

const STEPS = [
  { id: "service", label: "Service Selection" },
  { id: "questionnaire", label: "Questionnaire" },
  { id: "meeting", label: "Schedule Meeting" },
] as const;

type StepId = (typeof STEPS)[number]["id"];

export interface ServiceSelectionData {
  selectedPackageId: string | null;
  selectedVariantId: string | null;
  selectedAddons: string[];
  selectedRetainers: string[];
  budget: BudgetRange | "";
}

export interface QuestionnaireData {
  deliverables: string[];
  risks: string[];
  features: string[];
  ownership: string[];
  handoverProcess: string[];
  projectConstraints: string[];
}

export interface MeetingData {
  meetingDuration: 30 | 45 | 60;
  selectedSlot: AvailabilitySlot | null;
  discussionTopics: string;
}

export interface CreateSolutionFormData {
  service: ServiceSelectionData;
  questionnaire: QuestionnaireData;
  meeting: MeetingData;
}

const initialServiceData: ServiceSelectionData = {
  selectedPackageId: null,
  selectedVariantId: null,
  selectedAddons: [],
  selectedRetainers: [],
  budget: "",
};

const initialQuestionnaireData: QuestionnaireData = {
  deliverables: [],
  risks: [],
  features: [],
  ownership: [],
  handoverProcess: [],
  projectConstraints: [],
};

const initialMeetingData: MeetingData = {
  meetingDuration: 45,
  selectedSlot: null,
  discussionTopics: "",
};

export function CreateSolutionPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { getOfferingById } = usePackageOfferings();
  const { slots, isLoadingSlots, fetchAvailabilitySlots, isSubmitting } =
    useScheduledMeetings();

  const [currentStep, setCurrentStep] = useState<StepId>("service");
  const [formData, setFormData] = useState<CreateSolutionFormData>({
    service: initialServiceData,
    questionnaire: initialQuestionnaireData,
    meeting: initialMeetingData,
  });
  const [bookingComplete, setBookingComplete] = useState(false);

  useEffect(() => {
    if (currentStep === "meeting") {
      fetchAvailabilitySlots();
    }
  }, [currentStep, fetchAvailabilitySlots]);

  const currentStepIndex = STEPS.findIndex((s) => s.id === currentStep);

  const handleNext = () => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < STEPS.length) {
      setCurrentStep(STEPS[nextIndex].id);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleBack = () => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(STEPS[prevIndex].id);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleScheduleMeeting = async () => {
    if (!formData.meeting.selectedSlot) {
      toast({
        title: "Please select a time slot",
        description: "Choose an available date and time for your consultation.",
        variant: "destructive",
      });
      return;
    }

    try {
      setBookingComplete(true);
    } catch {
      toast({
        title: "Booking failed",
        description: "Unable to schedule meeting. Please try again.",
        variant: "destructive",
      });
    }
  };

  const selectedPackage = formData.service.selectedPackageId
    ? getOfferingById(formData.service.selectedPackageId)
    : null;

  if (bookingComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 py-24">
        <div className="absolute inset-0 hero-gradient" />
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-2xl w-full relative z-10"
        >
          <Card className="glass-card border-gradient text-center">
            <CardContent className="pt-12 pb-8 px-8">
              <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="h-10 w-10 text-emerald-400" />
              </div>
              <h1 className="font-display text-3xl md:text-4xl font-bold mb-4">
                Discovery meeting scheduled!
              </h1>

              {formData.meeting.selectedSlot && (
                <Card className="bg-primary/10 border-primary/30 mb-6">
                  <CardContent className="p-4">
                    <p className="font-semibold text-lg">
                      {new Date(
                        formData.meeting.selectedSlot.date,
                      ).toLocaleDateString("en-US", {
                        weekday: "long",
                        month: "long",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </p>
                    <p className="text-primary">
                      {formData.meeting.selectedSlot.availabilityWindow}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Duration: {formData.meeting.meetingDuration} minutes
                    </p>
                  </CardContent>
                </Card>
              )}

              <p className="text-muted-foreground mb-6">
                We'll send you a calendar invite and prepare a Statement of Work
                (SOW) contract based on your requirements.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button onClick={() => navigate("/service-orders")}>
                  Go to Service Orders
                </Button>
                <Button variant="outline" onClick={() => navigate("/")}>
                  Back to Portal
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 md:py-16">
      <div className="absolute inset-0 hero-gradient" />
      <div className="absolute inset-0 grid-background opacity-40" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/service-orders")}
            className="shrink-0"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>

        <div className="flex justify-center mb-8 md:mb-12">
          <div className="flex items-center gap-2 md:gap-4">
            {STEPS.map((step, index) => {
              const isCompleted = index < currentStepIndex;
              const isCurrent = step.id === currentStep;
              return (
                <div key={step.id} className="flex items-center gap-2 md:gap-4">
                  {index > 0 && (
                    <div
                      className={`h-px w-8 md:w-16 transition-colors ${
                        isCompleted ? "bg-primary" : "bg-border"
                      }`}
                    />
                  )}
                  <button
                    onClick={() => {
                      if (isCompleted) setCurrentStep(step.id);
                    }}
                    disabled={!isCompleted && !isCurrent}
                    className={`flex items-center gap-2 px-3 py-2 md:px-4 md:py-2 rounded-lg transition-all ${
                      isCurrent
                        ? "bg-primary text-primary-foreground"
                        : isCompleted
                          ? "bg-primary/20 text-primary cursor-pointer hover:bg-primary/30"
                          : "bg-muted text-muted-foreground"
                    }`}
                  >
                    <span
                      className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-semibold ${
                        isCompleted
                          ? "bg-primary text-primary-foreground"
                          : isCurrent
                            ? "bg-primary-foreground text-primary"
                            : "bg-muted-foreground/30"
                      }`}
                    >
                      {isCompleted ? (
                        <CheckCircle2 className="h-4 w-4" />
                      ) : (
                        index + 1
                      )}
                    </span>
                    <span className="hidden md:inline font-medium">
                      {step.label}
                    </span>
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <AnimatePresence mode="wait">
              {currentStep === "service" && (
                <motion.div
                  key="service"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                >
                  <ServiceSelectionStep
                    data={formData.service}
                    onChange={(data) =>
                      setFormData((prev) => ({ ...prev, service: data }))
                    }
                  />
                </motion.div>
              )}

              {currentStep === "questionnaire" && (
                <motion.div
                  key="questionnaire"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                >
                  <QuestionnaireStep
                    data={formData.questionnaire}
                    onChange={(data) =>
                      setFormData((prev) => ({ ...prev, questionnaire: data }))
                    }
                    onNext={handleNext}
                    onBack={handleBack}
                  />
                </motion.div>
              )}

              {currentStep === "meeting" && (
                <motion.div
                  key="meeting"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                >
                  <MeetingScheduleStep
                    data={formData.meeting}
                    onChange={(data) =>
                      setFormData((prev) => ({ ...prev, meeting: data }))
                    }
                    slots={slots}
                    isLoadingSlots={isLoadingSlots}
                    onSchedule={handleScheduleMeeting}
                    isSubmitting={isSubmitting}
                    onBack={handleBack}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-8 space-y-4">
              <Card className="glass-card border-gradient">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-lg mb-4">Project Budget</h3>
                  <Select
                    value={formData.service.budget}
                    onValueChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        service: {
                          ...prev.service,
                          budget: value as BudgetRange,
                        },
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select your budget range" />
                    </SelectTrigger>
                    <SelectContent>
                      {[
                        "$5,000 - $10,000",
                        "$10,000 - $25,000",
                        "$25,000 - $50,000",
                        "$50,000 - $100,000",
                        "$100,000+",
                      ].map((range) => (
                        <SelectItem key={range} value={range}>
                          {range}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </CardContent>
              </Card>

              <Card className="glass-card border-gradient">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-lg mb-4">Summary</h3>

                  <div className="space-y-4">
                    {formData.service.selectedPackageId === "custom" ? (
                      <div className="p-3 bg-primary/10 rounded-lg border border-primary/30">
                        <p className="text-xs text-muted-foreground mb-1">
                          Selected Service
                        </p>
                        <p className="font-semibold">Custom Solution</p>
                        <p className="text-sm text-muted-foreground">
                          Tailored to your requirements
                        </p>
                      </div>
                    ) : selectedPackage ? (
                      <div className="p-3 bg-primary/10 rounded-lg border border-primary/30">
                        <p className="text-xs text-muted-foreground mb-1">
                          Selected Package
                        </p>
                        <p className="font-semibold">
                          {selectedPackage.name}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {selectedPackage.pricing.priceRange
                            ? `$${selectedPackage.pricing.priceRange.min.toLocaleString()}${selectedPackage.pricing.priceRange.min !== selectedPackage.pricing.priceRange.max ? ` – $${selectedPackage.pricing.priceRange.max.toLocaleString()}` : ""} ${selectedPackage.pricing.type === "RECURRING" ? "/mo" : ""} USD`
                            : `$${selectedPackage.pricing.hourlyRate}/hr USD`}
                        </p>
                      </div>
                    ) : null}

                    {formData.service.budget && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">
                          Budget
                        </p>
                        <p className="font-medium">{formData.service.budget}</p>
                      </div>
                    )}

                    {formData.meeting.selectedSlot && (
                      <div className="p-3 bg-primary/10 rounded-lg border border-primary/30">
                        <p className="text-xs text-muted-foreground mb-1">
                          Meeting
                        </p>
                        <p className="font-semibold">
                          {new Date(
                            formData.meeting.selectedSlot.date,
                          ).toLocaleDateString("en-US", {
                            weekday: "short",
                            month: "short",
                            day: "numeric",
                          })}
                        </p>
                        <p className="text-sm text-primary">
                          {formData.meeting.selectedSlot.availabilityWindow}
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {currentStep === "service" && (
                <Button
                  onClick={handleNext}
                  disabled={
                    !formData.service.selectedPackageId ||
                    !formData.service.budget
                  }
                  size="lg"
                  className="w-full"
                >
                  Continue
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
